import React, { Fragment, useEffect, useState } from "react";

import { HashRouter as ApplicationRouter } from "react-router-dom";

import { connect } from "react-redux";

import Router from "../navigation/Router";

import Login from "./screens/login";
import firebase from "firebase";
import styled from "styled-components";
import Paper from "@material-ui/core/Paper";
import CircularProgress from "@material-ui/core/CircularProgress";

import User from "../types/StoreUser";

import { SET_FIREBASE_USER } from "../store/reducers/firebaseUser";
import {
  bootstrapAdminUser,
  completeAdminOnboarding,
} from "../database/user/adminUserApi";
import { updateCommunity } from "../database/community/updateCommunity";
import OnboardingWizard from "./screens/onboarding/OnboardingWizard";
import { ThemeScope } from "../design-system";
import {
  AI_PROMPT_SHARED_TAIL,
  GeneratedDraft,
  StarterContent,
} from "./screens/onboarding/OnboardingAPI";
import images from "../resources";
import DB_PATH from "../database/DB_PATH";
import { inferPracticeType } from "../types/Habit";
import { setPlan } from "../database/plans/setPlan";
import createDayPlan from "../database/dayPlan/createDayPlan";
import createDailyStep from "../database/dailyStep/createDailyStep";
import { uploadQuestionnaire } from "../database/questionnaire/uploadQuestionnaire";
import { DAILY_STEP_TYPE } from "../types/DailyStep";
import { createAuthor } from "../database/authors/createAuthor";
import { Questionnaire } from "../types/Questionnaire";

async function saveStarterContent(
  communityId: string,
  authorFullName: string,
  draft: GeneratedDraft,
  content: StarterContent
): Promise<string> {
  const db = firebase.firestore();
  const now = new Date().toISOString();

  // Author node
  const authorId = await createAuthor({
    id: "",
    fullName: authorFullName,
    profilePhotoURL: content.author?.profilePhotoURL ?? "",
    coverURL: "",
    description: content.author?.description ?? "",
    shortDescription: content.author?.shortDescription ?? "",
    tags: content.author?.tags ?? [],
    createdAt: Date.now(),
    communityId,
    voiceId: content.author?.voiceId ?? "",
    agentId: content.author?.agentId ?? "",
    aiGuidelines: content.author?.aiGuidelines ?? "",
  });

  // Plans → DayPlans → Steps
  for (let planIndex = 0; planIndex < content.plans.length; planIndex++) {
    const planDraft = content.plans[planIndex];
    const planId = db.collection(DB_PATH.PLAN).doc().id;
    await setPlan({
      id: planId,
      title: planDraft.title,
      shortDescription: planDraft.shortDescription,
      description: planDraft.description,
      level: planDraft.level,
      tags: planDraft.tags,
      communityId,
      authorId,
      coverImageURL: "",
      createdAt: now,
      categoryId: "",
      categoryName: "",
      authorName: authorFullName,
      position: planIndex + 1,
      isActive: true,
    });

    for (let di = 0; di < planDraft.dayPlans.length; di++) {
      const dayDraft = planDraft.dayPlans[di];
      const dayId = db.collection(DB_PATH.PLAN_DAY).doc().id;
      await createDayPlan({
        dayPlan: {
          id: dayId,
          title: dayDraft.title.replace(/^Day\s+\d+[:\-\s]*/i, "").trim(),
          description: dayDraft.description,
          planID: planId,
          position: di,
          communityId,
          createdAt: now,
          steps: [],
          isActive: true,
        },
      });

      for (let si = 0; si < dayDraft.steps.length; si++) {
        const stepDraft = dayDraft.steps[si];
        const stepId = db.collection(DB_PATH.PLAN_STEP).doc().id;
        await createDailyStep({
          dailyStep: {
            id: stepId,
            title: stepDraft.title,
            subtitle: stepDraft.subtitle,
            sectionTitle: stepDraft.sectionTitle,
            description: stepDraft.description,
            quote: "",
            backgroundImageURL: "",
            reference: "",
            type: DAILY_STEP_TYPE.DEFAULT,
            dayPlanID: dayId,
            planID: planId,
            authorID: authorId,
            authorName: authorFullName,
            communityId,
            position: si,
            learningStyle: stepDraft.learningStyle,
          },
        });
      }
    }
  }

  // Habits
  for (const habitDraft of content.habits) {
    const habitRef = db.collection(DB_PATH.HABIT).doc();
    const habitId = habitRef.id;
    await habitRef.set({
      id: habitId,
      habitName: habitDraft.habitName,
      habitDescription: habitDraft.habitDescription,
      targetFrequency: habitDraft.targetFrequency,
      // Use the AI's classification; fall back to deriving it from the actions
      // so the habit never lands on a bare DEFAULT.
      practiceType: habitDraft.practiceType ?? inferPracticeType(habitDraft.actions),
      communityId,
      creationDate: now,
      levels: [],
      learningPlansIds: [],
      backgroundImageURL: "",
      authorsIds: [authorId],
      status: true,
    });

    for (const actionDraft of habitDraft.actions ?? []) {
      const actionRef = db.collection(DB_PATH.HABIT_ACTION).doc();
      const actionId = actionRef.id;
      await actionRef.set({
        id: actionId,
        name: actionDraft.name,
        description: actionDraft.description,
        actionType: actionDraft.actionType,
        duration: actionDraft.duration ?? 0,
        goal: actionDraft.goal ?? "",
        habitId,
        communityId,
        createdAt: now,
      });

      for (const subactionDraft of actionDraft.subactions ?? []) {
        const subRef = db.collection(DB_PATH.HABIT_SUBACTION).doc();
        await subRef.set({
          id: subRef.id,
          name: subactionDraft.name,
          description: subactionDraft.description,
          actionType: subactionDraft.actionType,
          aiPrompt: subactionDraft.aiPrompt ?? "",
          duration: subactionDraft.duration ?? 0,
          intensityLevel: subactionDraft.intensityLevel ?? "medium",
          reflectionPrompt: subactionDraft.reflectionPrompt ?? "",
          habitId,
          actionId,
          communityId,
          createdAt: now,
        });
      }
    }
  }

  // Quotes
  for (const quoteDraft of content.quotes) {
    const quoteRef = db.collection(DB_PATH.QUOTE).doc();
    await quoteRef.set({
      id: quoteRef.id,
      quote: quoteDraft.quote,
      authorName: quoteDraft.authorName,
      likeCount: 0,
      communityId,
      createdAt: now,
      stepID: "",
    });
  }

  // Questionnaire
  const questionnairePayload: Questionnaire = {
    app: draft.title || "Community",
    version: "v1",
    categories: [],
    ai_prompt: `${content.questionnaire.aiPrompt.trim()}\n\n${AI_PROMPT_SHARED_TAIL.trim()}`,
    loading: {
      title: "Analyzing your answers",
      progress_label: "Building your personalized plan",
      alerts: [],
    },
    questions: content.questionnaire.questions as any,
    interstitials: [],
    beforeAfter: {
      before: content.questionnaire.beforeAfter?.before ?? [],
      after: content.questionnaire.beforeAfter?.after ?? [],
    },
    commit: {
      greeting: content.questionnaire.commit?.greeting ?? "Commit to your next chapter",
      commitments: content.questionnaire.commit?.commitments ?? [],
      subcopy:
        content.questionnaire.commit?.subcopy ??
        "Show up consistently, trust the process, and follow the plan.",
      needSignature: true,
      primaryCta: "Unlock My Plan",
    },
    reviews: content.questionnaire.reviews ?? [],
    ready: {
      headline: "Your personalized plan is ready",
      bullets: [
        "Built around your goals and current situation",
        "Focused on the highest-leverage next steps",
        "Designed to help you make progress faster",
      ],
      primaryCta: "Unlock My Plan",
    },
  };

  await uploadQuestionnaire(communityId, questionnairePayload);

  return authorId;
}

export type PendingOnboardingUser = {
  uid: string;
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  phoneNumber: string;
  communityId: string;
};

export async function bootstrapNewUser(user: Omit<PendingOnboardingUser, "communityId">): Promise<PendingOnboardingUser> {
  // AdminUser is server-owned: the callable generates the community id (clean,
  // number-free, unique) and writes the admin record with the Admin SDK, so a
  // caller can never attach themselves to a community they do not own. The id
  // doubles as the public landing slug (/c/{slug}).
  const { communityId } = await bootstrapAdminUser({
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    company: user.company,
    phoneNumber: user.phoneNumber,
  });

  return { ...user, communityId };
}

async function ensureOnboardingCommunity(
  user: Omit<PendingOnboardingUser, "communityId"> & { communityId?: string }
): Promise<PendingOnboardingUser> {
  if (user.communityId) {
    return { ...user, communityId: user.communityId };
  }

  return bootstrapNewUser({
    uid: user.uid,
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    company: user.company,
    phoneNumber: user.phoneNumber,
  });
}

type Props = {
  logUser: (user: any) => void;
  user: User;
  setIsMobile: (isMobile: boolean) => void;
};

const OnboardingScreen = styled.div.attrs({ className: "ds-root" })`
  flex: 1;
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-6);
  background: var(--color-bg);
  background-image:
    radial-gradient(
      circle at 20% 0%,
      var(--color-accent-subtle) 0%,
      transparent 40%
    ),
    radial-gradient(
      circle at 80% 100%,
      var(--color-accent-subtle) 0%,
      transparent 50%
    );
`;

const OnboardingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-5);
  width: 100%;
  max-width: 520px;
`;

const LogoLetter = styled.img`
  width: 96px;
  height: 96px;
`;

const OnboardingCard = styled(({ ...props }) => <Paper {...props} />)`
  width: 100%;
  padding: var(--space-8) var(--space-6) !important;
  background: var(--color-surface) !important;
  color: var(--color-text) !important;
  border: 1px solid var(--color-border) !important;
  border-radius: var(--radius-xl) !important;
  box-shadow: var(--shadow-lg) !important;
`;

const Root = ({ logUser, setIsMobile, user }: Props) => {
  const isExperimentsDemo =
    process.env.NODE_ENV === "development" &&
    new URLSearchParams(window.location.search).get("experimentsDemo") === "1";
  const [pendingOnboarding, setPendingOnboarding] =
    useState<PendingOnboardingUser | null>(null);
  const [communityCheckLoading, setCommunityCheckLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
      console.log("user", user);
      logUser(user);
    });
    return unsubscribe;
  }, []);

  function handleWindowSizeChange() {
    setIsMobile(window.innerWidth <= 768);
  }
  useEffect(() => {
    window.addEventListener("resize", handleWindowSizeChange);
    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, []);

  useEffect(() => {
    if (!user) {
      setCommunityCheckLoading(false);
      return;
    }
    if (pendingOnboarding) {
      setCommunityCheckLoading(false);
      return;
    }

    const firebaseUser = user as any;

    firebase
      .database()
      .ref(`/${DB_PATH.ADMIN_USER}/${firebaseUser.uid}`)
      .once("value")
      .then(async (snapshot) => {
        const userRecord = snapshot.val();
        if (!userRecord || userRecord.onboardingCompleted === false) {
          const displayName: string = firebaseUser.displayName || "";
          const [firstName, ...rest] = displayName.split(" ");
          const pendingUser = await ensureOnboardingCommunity({
            uid: firebaseUser.uid,
            firstName: userRecord?.firstName || firstName || "",
            lastName: userRecord?.lastName || rest.join(" ") || "",
            email: firebaseUser.email || userRecord?.email || "",
            company: userRecord?.company || "",
            phoneNumber: userRecord?.phoneNumber || "",
            communityId: userRecord?.communityId || "",
          });
          setPendingOnboarding((prev) => prev ?? pendingUser);
        }
        setCommunityCheckLoading(false);
      })
      .catch((error) => {
        console.error("Unable to check onboarding state", error);
        setCommunityCheckLoading(false);
      });
  }, [pendingOnboarding, user]);

  // Returns the created community id, which doubles as the public landing
  // slug (tribed.io/c/{slug}), so the wizard can show the live page link on
  // its success screen.
  const handleOnboardingComplete = async (
    draft: GeneratedDraft,
    _content?: StarterContent
  ): Promise<string> => {
    if (!pendingOnboarding) return "";

    const onboardingUser = await ensureOnboardingCommunity(pendingOnboarding);
    if (!onboardingUser.communityId) {
      throw new Error("Community id is required to launch your community.");
    }
    if (onboardingUser.communityId !== pendingOnboarding.communityId) {
      setPendingOnboarding(onboardingUser);
    }

    const { communityId, uid } = onboardingUser;
    const authorFullName = `${pendingOnboarding.firstName} ${pendingOnboarding.lastName}`;
    let authorId = uid;

    if (_content) {
      authorId = await saveStarterContent(
        communityId,
        authorFullName,
        draft,
        _content
      );
    }

    await updateCommunity({
      id: communityId,
      title: draft.title,
      description: draft.description,
      shortDescription: draft.shortDescription,
      categoryName: draft.categoryName,
      categoryId: draft.categoryName.toLowerCase().replace(/\s+/g, "-"),
      tags: draft.tags,
      writingStyle: draft.writingStyle,
      visualStyle: draft.visualStyle,
      primaryColor: draft.primaryColor ?? "",
      secondaryColor: draft.secondaryColor ?? "",
      logoURL: draft.logoURL ?? "",
      lang: draft.lang ?? "",
      stylePack: draft.stylePack ?? "",
      status: "active",
      coverURL: "",
      imagesURL: [],
      createdAt: new Date().toISOString(),
      author: {
        id: authorId,
        fullName: authorFullName,
        voiceId: _content?.author?.voiceId ?? "",
        agentId: _content?.author?.agentId ?? "",
        profilePhotoURL: _content?.author?.profilePhotoURL ?? "",
        description: _content?.author?.description ?? draft.authorDescription,
        aiGuidelines: _content?.author?.aiGuidelines ?? draft.aiGuidelines,
      },
    });

    await completeAdminOnboarding();

    // Note: we intentionally do NOT clear pendingOnboarding here. The wizard
    // shows a success screen with the live page link and then calls
    // onFinished, which hands off to the dashboard.
    return communityId;
  };

  // Called by the wizard after its success screen, to load the dashboard
  // (the user is already authenticated).
  const finishOnboarding = () => setPendingOnboarding(null);

  const renderDashboard = () => (
    <ApplicationRouter>
      <Fragment>
        <Router />
      </Fragment>
    </ApplicationRouter>
  );

  // Keep the local experiments preview shareable without weakening production
  // authentication. Experiment data is synthetic whenever this flag is active.
  if (isExperimentsDemo && !user) {
    return renderDashboard();
  }

  // Onboarding intercepts BEFORE dashboard — survives Firebase auth state
  // changes. Forced dark so the funnel matches the tribed.io landing look
  // regardless of the admin theme preference.
  if (pendingOnboarding) {
    return (
      <ThemeScope mode="dark">
        <OnboardingScreen>
          <OnboardingWrapper>
            <LogoLetter src={images.logo_letters} />
            <OnboardingCard>
              <OnboardingWizard
                userName={pendingOnboarding.firstName}
                userId={pendingOnboarding.uid}
                onComplete={handleOnboardingComplete}
                onFinished={finishOnboarding}
              />
            </OnboardingCard>
          </OnboardingWrapper>
        </OnboardingScreen>
      </ThemeScope>
    );
  }

  if (communityCheckLoading && user) {
    return (
      <ThemeScope mode="dark">
        <OnboardingScreen>
          <CircularProgress style={{ color: "var(--color-accent)" }} />
        </OnboardingScreen>
      </ThemeScope>
    );
  }

  // Login/signup is the first screen of the tribed.io funnel, so it gets the
  // same forced-dark landing look as the onboarding wizard. The dashboard
  // afterwards keeps the admin's own theme preference.
  return (
    <Fragment>
      {user ? (
        renderDashboard()
      ) : (
        <ThemeScope mode="dark">
          <Login onNewUserCreated={setPendingOnboarding} />
        </ThemeScope>
      )}
    </Fragment>
  );
};

const mapStateToProps = (state) => ({
  user: state.firebaseUser,
});
const mapDispatchToProps = (dispatch) => {
  return {
    logUser: (value) => dispatch({ type: SET_FIREBASE_USER, payload: value }),
    setIsMobile: (isMobile) =>
      dispatch({ type: "SET_IS_MOBILE", payload: isMobile }),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Root);
